<template>
  <!-- Winner Card -->
  <div v-if="variant === 'winner'" class="app-product-aid-card app-product-aid-card--winner">
    <div class="app-product-aid-card__progress-bar">
      <div class="app-product-aid-card__progress-fill" :style="{ width: `${percentage}%` }">
        <span class="app-product-aid-card__progress-text">{{ percentage }}%</span>
      </div>
    </div>

    <AppProductAidCardBody
      :title
      :subtitle
      :image
      :link
      :is-regional
      show-title
      class="app-product-aid-card__body--winner"
      @select-regional="$emit('select-regional')"
    />
  </div>

  <!-- Secondary / Regional Card -->
  <div v-else class="app-product-aid-card app-product-aid-card--secondary">
    <button
      class="app-product-aid-card__header"
      :class="[`app-product-aid-card__header--${variant}`]"
      @click="$emit('toggle')"
    >
      <span
        v-if="variant === 'secondary'"
        class="app-product-aid-card__percentage"
        :style="{ width: getPercentageWidth(percentage), '--percentage-max-width': `${PERCENTAGE_MAX_WIDTH}px` }"
      >
        {{ percentage }}%
      </span>

      <span class="app-product-aid-card__header-title h4">{{ title }}</span>

      <span class="app-product-aid-card__header-icon">
        <TappIcon
          icon="chevron-down"
          :size="20"
          class="app-product-aid-card__chevron"
          :class="{ 'app-product-aid-card__chevron--expanded': expanded }"
        />
      </span>
    </button>

    <AppProductAidCardBody
      v-if="expanded"
      :subtitle
      :image
      :link
      :is-regional
      @select-regional="$emit('select-regional')"
    />
  </div>
</template>

<script setup lang="ts">
  // Max width for percentage bar (in pixels) - change this single value to adjust both JS and CSS
  const PERCENTAGE_MAX_WIDTH = 120;

  defineProps<{
    variant: 'winner' | 'secondary' | 'regional';
    title: string;
    subtitle?: string;
    percentage?: number;
    image?: string;
    link?: string;
    isRegional?: boolean;
    expanded?: boolean;
  }>();

  defineEmits<{
    'select-regional': [];
    toggle: [];
  }>();

  function getPercentageWidth(percentage: number | undefined) {
    return `${((percentage || 0) / 100) * PERCENTAGE_MAX_WIDTH}px`;
  }
</script>

<style lang="postcss" scoped>
  .app-product-aid-card {
    overflow: hidden;
    border-radius: var(--spacing-1);
    background-color: var(--colors-white);

    &__progress-bar {
      height: var(--spacing-12);
      background: rgb(255 140 230 / 40%);
    }

    &__progress-fill {
      display: flex;
      align-items: center;
      justify-content: center;

      height: 100%;

      background: var(--colors-secondary-500);
    }

    &__header {
      display: grid;
      grid-template-columns: auto 1fr auto;
      width: 100%;

      &--regional {
        grid-template-columns: 1fr auto;
      }
    }

    &__header-title {
      display: flex;
      align-items: center;

      min-height: var(--spacing-12);
      padding: var(--spacing-1) var(--spacing-4);

      text-align: left;
    }

    &__header-icon {
      display: flex;
      align-items: center;
      padding: var(--spacing-1) var(--spacing-2);
    }

    &__percentage {
      display: flex;
      align-items: center;
      justify-content: center;

      min-width: var(--spacing-12);
      max-width: var(--percentage-max-width);
      height: 100%;

      font-weight: 600;
      white-space: nowrap;

      background: rgb(255 140 230);

      transition: width 0.3s ease-out;
    }
  }
</style>
